/**
 * What an aggregated invoice line bills, as the line itself reports it.
 *
 * An invoice line item's price and quantity can cover several hundred radios
 * at once, each billed as a `BilledEntity`. The line carries only the figures
 * — a total and a count per kind of entity — so reading an invoice never loads
 * what it billed; the entities themselves are listed from the billed-entity
 * grid on demand.
 *
 * Contains:
 * - getBilledEntitiesCount — the line's total billed-entity count
 * - summarizeBilledEntityKinds — the line's per-kind counts, each with the
 *   translation key naming that kind, ordered for display
 */

/**
 * Translation key naming each kind of entity a line can bill. Keyed by the
 * ContentType model name the server reports. Sites, groups and customers read
 * through the shared entity vocabulary so they are named as they are
 * everywhere else.
 */
export const BILLED_ENTITY_KIND_LABEL_KEYS = {
  radio: 'billing.entityKinds.radio',
  featureassociation: 'billing.entityKinds.feature',
  site: 'general.site',
  sitegroup: 'general.siteGroup',
  customer: 'general.customer',
};

/**
 * @param {{ billed_entities_count?: number|null }} item
 * @returns {number} the line's total billed-entity count, 0 when absent
 */
export function getBilledEntitiesCount(item) {
  return item.billed_entities_count ?? 0;
}

/**
 * The per-kind counts behind a line's total, ready to render.
 *
 * Largest count first, then alphabetically by kind, so a line reads
 * "300 radios, 2 groups" rather than in whatever order the payload arrived.
 * A kind the server does not name is still returned, keyed by its own name,
 * so a new entity kind shows up as a count rather than vanishing.
 *
 * @param {{ billed_entities_breakdown?: Object<string, number>|null }} item
 * @returns {Array<{ kind: string, count: number, labelKey: string }>}
 */
export function summarizeBilledEntityKinds(item) {
  const breakdown = item.billed_entities_breakdown ?? {};
  return Object.entries(breakdown)
    .filter(([, count]) => count > 0)
    .map(([kind, count]) => ({
      kind,
      count,
      labelKey: BILLED_ENTITY_KIND_LABEL_KEYS[kind] ?? kind,
    }))
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind));
}
